import { Spotlight } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import { useConditionalCoordination } from '@databases/coordination';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { useOnClickOutsideRef } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

export const QUICK_CALC_ONBOARDING = 'quickCalcOnboarding';
export const QUICK_CALC_ONBOARDING_COORDINATION_MESSAGE_KEY =
  'cc-databases-quick-calc-onboarding' as const;

export const VIEWS_CHANGEBOARDING = 'viewsChangeboarding';
export const VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY =
  'cc-databases-views-changeboarding' as const;

type CoordinationMessageKeys =
  | typeof VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY
  | typeof QUICK_CALC_ONBOARDING_COORDINATION_MESSAGE_KEY;

const ONBOARDING_TOUR_DIALOG_WIDTH = 287;
const VIEWS_CHANGEBOARDING_DOCUMENTATION =
  'https://support.atlassian.com/confluence-cloud/docs/database-views/';

const spotlightLink = xcss({
  color: 'color.text.inverse',
  display: 'inline',
  textDecoration: 'underline',

  ':hover': {
    color: 'color.text.inverse',
    display: 'inline',
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
});

export const DatabaseSpotlight = ({
  coordinationMessageKey,
  hasSelection = false,
  innerContainerRef = null,
  isEnabled,
}: {
  coordinationMessageKey: CoordinationMessageKeys;
  hasSelection?: boolean;
  innerContainerRef?: HTMLElement | null;
  isEnabled?: boolean | undefined;
}) => {
  const { formatMessage } = useIntl();
  const { isPanelVisible } = usePanel();
  const dispatch = defaultStoreUtils.useDispatch();
  const [coordinationStarted, stop] = useConditionalCoordination(
    coordinationMessageKey,
    isEnabled ?? true
  );

  const [activeSpotlight, setActiveSpotlight] = useState<null | number>(0);
  useOnClickOutsideRef(
    innerContainerRef,
    hasSelection &&
      (activeSpotlight === null || activeSpotlight === 0) &&
      !isPanelVisible(),
    () => {
      dispatch(deselectAll());
    },
    true
  );

  // Additional constants for spotlight actions
  // const advanceSpotlight = formatMessage(messages['onboarding-tour.advance-spotlight']);
  // const dismissSpotlight = formatMessage(messages['onboarding-tour.dismiss-spotlight']);
  const completeSpotlight = formatMessage(messages['databases.ga.spotlight.ok']);

  const start = () => setActiveSpotlight(0);
  // Additional spotlight step
  // const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);
  const end = useCallback(() => {
    stop();
    setActiveSpotlight(null);
  }, [stop]);

  useHandleOutsideClick(end, undefined, ['[data-testid="quickCalcOnboarding--dialog"]']);

  useEffect(() => {
    if (coordinationStarted) {
      start();
    }
  }, [coordinationStarted]);

  const changeboardingSpotlights = [
    <Spotlight
      actions={[
        {
          onClick: () => end(),
          text: completeSpotlight,
        },
      ]}
      heading={formatMessage(messages['changeboarding.views-heading'])}
      key={VIEWS_CHANGEBOARDING}
      target={VIEWS_CHANGEBOARDING}
      testId={VIEWS_CHANGEBOARDING}
      dialogWidth={ONBOARDING_TOUR_DIALOG_WIDTH}
      pulse
    >
      <Box>
        <FormattedMessage
          {...messages['changeboarding.views-message']}
          values={{
            // @ts-expect-error
            a: (chunks: React.ReactNode) => (
              <Anchor
                href={VIEWS_CHANGEBOARDING_DOCUMENTATION}
                target="_blank"
                rel="noreferrer"
                xcss={spotlightLink}
              >
                {chunks}
              </Anchor>
            ),
          }}
        />
      </Box>
    </Spotlight>,
  ];
  const quickCalcOnboardingSpotlights = [
    <Spotlight
      actions={[
        {
          onClick: () => end(),
          text: completeSpotlight,
        },
      ]}
      heading={formatMessage(messages['onboarding.quick-calc-heading'])}
      key={QUICK_CALC_ONBOARDING}
      target={QUICK_CALC_ONBOARDING}
      testId={QUICK_CALC_ONBOARDING}
      dialogWidth={ONBOARDING_TOUR_DIALOG_WIDTH}
      dialogPlacement="right bottom"
      pulse
    >
      <Box>
        <FormattedMessage {...messages['onboarding.quick-calc-message']} />
      </Box>
    </Spotlight>,
  ];

  const getSpotlights = (coordinationMessageKey: CoordinationMessageKeys) => {
    if (coordinationMessageKey === VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY) {
      return changeboardingSpotlights;
    } else if (
      coordinationMessageKey === QUICK_CALC_ONBOARDING_COORDINATION_MESSAGE_KEY
    ) {
      return quickCalcOnboardingSpotlights;
    }
  };

  const renderActiveSpotlight = () => {
    if (activeSpotlight === null) {
      return null;
    }

    const spotlights = getSpotlights(coordinationMessageKey);
    return spotlights ? spotlights[activeSpotlight] : undefined;
  };

  return <>{coordinationStarted && renderActiveSpotlight()}</>;
};
